import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Database, Shield, Server, HardDrive, Save,
  CheckCircle, AlertTriangle, XCircle, Clock, Activity
} from 'lucide-react';
import api from '../../services/api';
import { notifySuccess, notifyError, notifyInfo } from '../../utils/notifications';

const HEALTH_COLORS = {
  OK: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  WARNING: 'bg-amber-50 border-amber-300 text-amber-900',
  CRITICAL: 'bg-red-50 border-red-300 text-red-900',
  UNKNOWN: 'bg-slate-50 border-slate-300 text-slate-900',
};

const HEALTH_ICONS = {
  OK: { Icon: CheckCircle, color: 'text-emerald-600' },
  WARNING: { Icon: AlertTriangle, color: 'text-amber-600' },
  CRITICAL: { Icon: XCircle, color: 'text-red-600' },
  UNKNOWN: { Icon: AlertTriangle, color: 'text-slate-600' },
};

const formatAge = (hours) => {
  if (hours == null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} j`;
};

const Card = ({ title, icon: Icon, health = 'UNKNOWN', children, action }) => {
  const { Icon: HealthIcon, color } = HEALTH_ICONS[health];
  return (
    <div className={`rounded-lg border-2 p-5 ${HEALTH_COLORS[health]}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={20} />}
          <h3 className="font-semibold text-base">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <HealthIcon size={20} className={color} />
          {action}
        </div>
      </div>
      <div className="space-y-1.5 text-sm">{children}</div>
    </div>
  );
};

const Row = ({ label, value, sub }) => (
  <div className="flex justify-between items-baseline">
    <span className="text-slate-600">{label}</span>
    <span className="font-medium text-right">
      {value}
      {sub && <span className="block text-xs text-slate-500">{sub}</span>}
    </span>
  </div>
);

const SystemeStatus = () => {
  const [backup, setBackup] = useState(null);
  const [systeme, setSysteme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [bResp, sResp] = await Promise.all([
        api.get('/admin/backup/status').catch(e => ({ data: null, error: e })),
        api.get('/admin/systeme/status').catch(e => ({ data: null, error: e })),
      ]);
      if (bResp.data) setBackup(bResp.data);
      if (sResp.data) setSysteme(sResp.data);
      setLastRefresh(new Date());
    } catch (e) {
      notifyError('Erreur chargement status systeme');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 60000);
    return () => clearInterval(id);
  }, [loadAll]);

  const triggerBackup = async () => {
    if (triggering) return;
    setTriggering(true);
    notifyInfo('Backup en cours...');
    try {
      const resp = await api.post('/admin/backup/trigger-now');
      if (resp.data?.success) {
        notifySuccess(resp.data.message || 'Backup termine');
        await loadAll();
      } else {
        notifyError(resp.data?.error || 'Backup echec');
      }
    } catch (e) {
      const msg = e?.response?.data?.error || 'Backup impossible';
      notifyError(msg, { status: e?.response?.status });
    } finally {
      setTriggering(false);
    }
  };

  const backupHealth = backup?.health || 'UNKNOWN';
  const serviceHealth = systeme?.service?.active ? 'OK' : 'CRITICAL';
  const dbHealth = systeme?.db?.connected ? 'OK' : 'CRITICAL';
  const sec = systeme?.securite || {};
  const secHealth = (sec.rate_limit_active && sec.security_headers && sec.jwt_enabled) ? 'OK' : 'WARNING';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity size={24} /> Etat systeme
          </h1>
          {lastRefresh && (
            <p className="text-sm text-slate-500 mt-1">
              Dernier refresh : {lastRefresh.toLocaleTimeString()} · auto-refresh 60s
            </p>
          )}
        </div>
        <button
          onClick={loadAll}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-300 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        <Card
          title="Backups"
          icon={HardDrive}
          health={backupHealth}
          action={
            <button
              onClick={triggerBackup}
              disabled={triggering}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md disabled:opacity-50"
            >
              <Save size={14} className={triggering ? 'animate-pulse' : ''} />
              {triggering ? 'En cours...' : 'Sauvegarder maintenant'}
            </button>
          }
        >
          {backup?.last_local ? (
            <Row
              label="Dernier backup local"
              value={`Il y a ${formatAge(backup.last_local.age_hours)}`}
              sub={`${backup.last_local.size_mb} MB · ${backup.last_local.filename}`}
            />
          ) : (
            <Row label="Dernier backup local" value="Aucun" />
          )}
          <Row label="Backups locaux" value={backup?.nb_local_backups ?? '—'} />
          <Row label="Backups GDrive" value={backup?.nb_gdrive_backups ?? '—'} />
          <Row
            label="Cron auto"
            value={backup?.cron_active
              ? <span className="text-emerald-700">Actif</span>
              : <span className="text-red-700">Inactif</span>}
          />
          {backup?.last_gdrive && (
            <Row label="Dernier GDrive" value="" sub={backup.last_gdrive} />
          )}
        </Card>

        <Card title="Service Flask" icon={Server} health={serviceHealth}>
          <Row
            label="Etat"
            value={systeme?.service?.active
              ? <span className="text-emerald-700 font-semibold">active (running)</span>
              : <span className="text-red-700 font-semibold">inactive</span>}
          />
          <Row label="Main PID" value={systeme?.service?.main_pid ?? '—'} />
          <Row label="Demarre" value={systeme?.service?.uptime ?? '—'} />
          <Row label="Version" value={systeme?.version ?? '—'} />
        </Card>

        <Card title="Base de donnees" icon={Database} health={dbHealth}>
          <Row
            label="Connexion"
            value={systeme?.db?.connected
              ? <span className="text-emerald-700">OK</span>
              : <span className="text-red-700">KO</span>}
          />
          <Row label="Taille DB" value={`${systeme?.db?.size_mb ?? 0} MB`} />
          <Row label="Tables" value={systeme?.db?.nb_tables ?? '—'} />
          <Row label="Bijoux" value={systeme?.db?.nb_bijoux?.toLocaleString() ?? '—'} />
          <Row label="Clients" value={systeme?.db?.nb_clients?.toLocaleString() ?? '—'} />
          <Row label="Ventes" value={systeme?.db?.nb_ventes?.toLocaleString() ?? '—'} />
        </Card>

        <Card title="Securite" icon={Shield} health={secHealth}>
          <Row
            label="Headers securite (CSP/HSTS/X-Frame)"
            value={sec.security_headers
              ? <span className="text-emerald-700">Actifs</span>
              : <span className="text-red-700">Manquants</span>}
          />
          <Row
            label="Rate limiting"
            value={sec.rate_limit_active
              ? <span className="text-emerald-700">Actif (1000/h)</span>
              : <span className="text-red-700">Desactive</span>}
          />
          <Row
            label="JWT"
            value={sec.jwt_enabled
              ? <span className="text-emerald-700">Actif</span>
              : <span className="text-red-700">Desactive</span>}
          />
          <Row
            label="CORS"
            value={sec.cors_enabled
              ? <span className="text-emerald-700">Configure</span>
              : <span className="text-amber-700">Non configure</span>}
          />
        </Card>

      </div>

      {systeme?.timestamp && (
        <p className="text-xs text-slate-400 mt-4 flex items-center gap-1">
          <Clock size={12} />
          Donnees a {new Date(systeme.timestamp).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default SystemeStatus;
